import {Stack, createStyles} from '@mantine/core';
import React from 'react';

import PrimitiveValueEditor, {SupportedLeafType} from 'data-frames/PrimitiveValueEditor';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {PlateMetadataFieldEntry, PlateMetadataFieldsAction} from './ScratchPlateDesignModalUtil';

export interface PlateSchemaFieldsSectionProps {
  fields: ReadonlyArray<PlateMetadataFieldEntry>;
  onAction: (action: PlateMetadataFieldsAction) => void;
}

/**
 * Renders a value input per field on the selected plate schema (name and type are fixed by the
 * schema, so — unlike PlateMetadataFieldsSection — there's no add/remove/rename/retype UI here.
 */
export default function PlateSchemaFieldsSection({
  fields,
  onAction,
}: PlateSchemaFieldsSectionProps): JSX.Element | null {
  const {classes} = useStyles();

  if (fields.length === 0) {
    return null;
  }

  return (
    <Stack spacing="xs">
      <FieldLabel label="Plate schema fields" labelStyle={FieldLabel.STYLES.LABEL} />
      {fields.map((field) => (
        <Stack key={field.id} spacing="xs" className={classes.fieldBox}>
          <FieldLabel label={field.name} />
          <div className={classes.valueEditor} role="group" aria-label={field.name}>
            <PrimitiveValueEditor
              // Remount on type change so the editor's internal value state can't carry a value of
              // a previous, now type-incompatible, primitive type into the new editor.
              key={field.type}
              // Safe cast: entries are built from isPlateSchemaFieldCompatible-filtered fields.
              leafType={field.type as SupportedLeafType}
              value={field.value}
              onChange={(value) => {
                onAction({type: 'updateValue', id: field.id, value});
              }}
            />
          </div>
        </Stack>
      ))}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  fieldBox: {
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.sm,
    padding: theme.spacing.sm,
  },
  // PrimitiveValueEditor's internal input/button heights are hardcoded by the generalized step
  // config editor it delegates to and aren't exposed as a prop, so height is normalized here to
  // match other inputs in this modal (mirrors PlateMetadataFieldsSection).
  valueEditor: {
    '& > div': {
      marginBottom: 0,
    },
    '& input, & button': {
      height: '42px !important',
      fontSize: theme.fontSizes.md,
    },
  },
}));
